export const flagToLocaleMap: Record<string, string> = {
  '🇩🇪': 'de',
  '🇬🇧': 'en',
  '🇫🇷': 'fr',
  '🇭🇺': 'hu',
};

export const localeToFlagMap = Object.fromEntries(
  Object.entries(flagToLocaleMap).map(([flag, locale]) => [locale, flag]),
);

export function parseMultilingualString(text: string | undefined): Record<string, string> {
  if (!text) return {};

  const regex = /(🇩🇪|🇬🇧|🇫🇷|🇭🇺)/g;
  const parts = text.split(regex).filter((part) => part.trim() !== '');

  const result: Record<string, string> = {};
  let currentLocale = 'de';
  let currentText = '';

  for (const part of parts) {
    if (flagToLocaleMap[part]) {
      if (currentText.trim()) result[currentLocale] = currentText.trim();
      currentLocale = flagToLocaleMap[part];
      currentText = '';
    } else {
      currentText += part + ' ';
    }
  }

  if (currentText.trim()) {
    result[currentLocale] = currentText.trim();
  }

  return result;
}

export function buildMultilingualString(translations: Record<string, string>): string {
  let output = translations['de'] ?? '';

  for (const [localeKey, text] of Object.entries(translations)) {
    if (localeKey === 'de') continue;
    const flag = localeToFlagMap[localeKey];
    if (text && flag) {
      output += ` ${flag} ${text}`;
    }
  }

  return output.trim();
}

export function getTextForLocaleStatic(text: string | undefined, locale: string): string {
  const translations = parseMultilingualString(text);
  return translations[locale] ?? translations['de'] ?? '';
}

export function getAvailableLocales(text: string | undefined): string[] {
  return Object.keys(parseMultilingualString(text));
}

/**
 * Recursively translates all string values in an object using getTextForLocaleStatic.
 */
export function translateMultilingualValuesInObject<T>(input: T, locale: string): T {
  if (typeof input === 'string') {
    // We know the result is still a string
    return getTextForLocaleStatic(input, locale) as unknown as T;
  }

  if (Array.isArray(input)) {
    // Map over array and translate each item recursively
    return input.map((item) => translateMultilingualValuesInObject(item, locale)) as unknown as T;
  }

  if (input !== null && typeof input === 'object') {
    const output: Record<string, unknown> = {};
    for (const key of Object.keys(input)) {
      const value = (input as Record<string, unknown>)[key];
      output[key] = translateMultilingualValuesInObject(value, locale);
    }
    return output as T;
  }

  // Leave all non-objects and non-strings unchanged
  return input;
}

/**
 * Translates all string fields in an array of objects using getTextForLocaleStatic.
 */
export function translateMultilingualArray<T extends Record<string, any>>(
  items: T[],
  locale: string,
): T[] {
  return items.map((item) => translateMultilingualValuesInObject(item, locale));
}
